import React, { useCallback, useMemo } from 'react';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Select, Stack } from '@lyearn/ui';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';
import useWorkspaceAccessControl from '@/components/WorkspaceAccessControl/hooks/useWorkspaceAccessControl';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes/index';
import {
  ExternalEventCreationStrategyEnum,
  ExternalEventProviderInfoInput,
  FormComponentFieldTypeEnum,
  FormComponentType,
  WorkspaceActions,
  WorkspaceSubjects,
} from '@/types/schema';

import { getProviderMetaObj } from '../../utils';
import ProviderFormInputs from './ProviderFormInputs';
import SelectProvider from './SelectProvider';
import { rootContainerStyles } from './styles';
import { ExternalEventProps, ExternalEventProviderType } from './types';
import {
  getCreationStrategyOptions,
  getProvider,
  isExternalProviderEmpty,
  LY_EVENT_URL_KEY,
} from './utils';

const getDefaultUrlField = (t: TFunction): FormComponentType => ({
  label: t('Events:Editor.SessionUrl'),
  name: LY_EVENT_URL_KEY,
  placeholder: t('Events:Editor.SessionUrlPlaceholder'),
  type: FormComponentFieldTypeEnum.Url,
});

const getCreationStrategyValue = (
  value: ExternalEventProviderInfoInput,
  showCreationStrategies: boolean,
) => {
  const meta = getProviderMetaObj(value);

  if (!showCreationStrategies || meta[LY_EVENT_URL_KEY]) {
    return ExternalEventCreationStrategyEnum.Form;
  }

  return ExternalEventCreationStrategyEnum.Integration;
};

const hasIntegrationCreationStrategy = (provider?: ExternalEventProviderType) => {
  return provider?.externalEventCreationStrategies.includes(
    ExternalEventCreationStrategyEnum.Integration,
  );
};

const useEventPermissions = () => {
  const isAdminRoute = useAdminRoute();

  const { canAccess: canCreateEvent } = useWorkspaceAccessControl(
    WorkspaceActions.Create,
    WorkspaceSubjects.Event,
  );

  return isAdminRoute ? true : canCreateEvent;
};

function ExternalEvent({
  isEditMode,
  error,
  providers,
  value,
  onChange,
  disabled,
}: ExternalEventProps) {
  const { t } = useTranslation();
  const canCreateEvent = useEventPermissions();

  const onProviderChange = useCallback(
    (providerValue: string) => {
      const selectedProviderData = getProvider(providerValue, providers);
      const showCreationStrategies = hasIntegrationCreationStrategy(selectedProviderData);

      onChange({
        ...value,
        providerId: providerValue,
        creationStrategy: getCreationStrategyValue(
          value,
          Boolean(isEditMode ? showCreationStrategies : canCreateEvent && showCreationStrategies),
        ),
      });
    },
    [canCreateEvent, isEditMode, onChange, providers, value],
  );

  const onCreationStrategyChange = useCallback(
    (creationStrategySelectedVal: string) => {
      onChange({
        ...value,
        creationStrategy: creationStrategySelectedVal as ExternalEventCreationStrategyEnum,
      });
    },
    [onChange, value],
  );

  const { currentProvider, showCreationStrategies } = useMemo(() => {
    const currentProvider = getProvider(value.providerId, providers);
    const showCreationStrategies = hasIntegrationCreationStrategy(currentProvider);

    return {
      currentProvider,
      showCreationStrategies: Boolean(
        isEditMode ? showCreationStrategies : canCreateEvent && showCreationStrategies,
      ),
    };
  }, [canCreateEvent, isEditMode, providers, value.providerId]);

  const formInputs = useMemo(() => {
    if (currentProvider?.formInputs) {
      return currentProvider?.formInputs;
    }

    return [getDefaultUrlField(t)];
  }, [currentProvider?.formInputs, t]);

  const isProviderEmpty = isExternalProviderEmpty(value);
  const creationStrategyOptions = useMemo(() => getCreationStrategyOptions(t), [t]);

  return (
    <Stack className={cn('grid gap-y-32', rootContainerStyles)} direction="col">
      <SelectProvider
        disabled={isEditMode || disabled}
        error={isProviderEmpty && error ? t('Events:Editor.ProviderEmptyError') : undefined}
        providers={providers}
        value={value.providerId}
        onChange={onProviderChange}
      />
      {showCreationStrategies ? (
        <Stack className="meta-field flex-1 space-y-8" direction="col">
          <Stack className="h-22" />
          <Select
            disableClearable
            disabled={isEditMode || disabled}
            getOptionLabel={getSelectOptionLabel}
            options={creationStrategyOptions}
            size="large"
            value={value.creationStrategy || undefined}
            onChange={onCreationStrategyChange}
          />
        </Stack>
      ) : null}
      {value.creationStrategy !== ExternalEventCreationStrategyEnum.Integration ? (
        <ProviderFormInputs
          disabled={disabled}
          formInputs={formInputs}
          value={value}
          onChange={onChange}
        />
      ) : null}
    </Stack>
  );
}

export default React.memo(ExternalEvent);
